import { List, ListItem, ListItemText, Typography } from "@mui/material";
import { Container, Box } from "@/components/atoms/index";
import changelogData from "./changelog.json";
import currentState from "./current-state.json";
import { useEffect, useMemo, useState } from "react";
import { startOfWeek, endOfWeek, format } from "date-fns";

interface ChangelogEntry {
  date: string;
  notes: string[];
}
function isValidDate(dateString: string) {
  const date = new Date(dateString + "T00:00:00Z");
  return !isNaN(date.getTime());
}

export const ChangelogPage = () => {
  const [selectedWeek, setSelectedWeek] = useState<string | null>(null);

  // Sort changelog data
  const sortedChangelogData = useMemo(() => {
    return [...changelogData].sort((a, b) => {
      return new Date(b.date).getTime() - new Date(a.date).getTime();
    });
  }, []);

  // Group entries by week
  const groupedByWeek = useMemo(() => {
    const groups: Record<string, ChangelogEntry[]> = {};

    sortedChangelogData.forEach(entry => {
      const entryDate = new Date(entry.date + "T00:00:00Z");
      const weekStart = startOfWeek(entryDate, { weekStartsOn: 1 }); // Monday
      const weekEnd = endOfWeek(entryDate, { weekStartsOn: 1 }); // Sunday

      const weekLabel = `${format(weekStart, "MMM dd")} - ${format(weekEnd, "MMM dd, yyyy")}`;

      if (!groups[weekLabel]) {
        groups[weekLabel] = [];
      }
      groups[weekLabel].push(entry);
    });

    return groups;
  }, [sortedChangelogData]);

  const selectedEntries = useMemo(() => {
    if (selectedWeek === "current state") {
      return currentState;
    }
    return selectedWeek ? groupedByWeek[selectedWeek] : null;
  }, [selectedWeek, groupedByWeek]);

  useEffect(() => {
    const firstWeek = Object.keys(groupedByWeek)[0];
    if (firstWeek) {
      setSelectedWeek(firstWeek);
    }
  }, [groupedByWeek]);

  return (
    <Container componentName={`changelog-page`}>
      <Box
        componentName={`changelog-parent-box`}
        sx={{
          display: "flex",
          flexDirection: "column",
          m: 2,
          flexGrow: 1,
        }}
      >
        <Typography
          variant="h3"
          align="center"
          color="primary.dark"
          sx={{
            my: 4,
            lineHeight: 2,
            textTransform: "capitalize",
            letterSpacing: 2,
            wordSpacing: 2,
          }}
        >
          What&apos;s New?
        </Typography>

        <Box
          componentName={`changelog-content-box`}
          flexDirection="row"
          border={false}
          sx={{
            gap: 2,
            display: "flex",
            padding: 2,
            flexDirection: "row",
          }}
        >
          {/* Left Panel */}
          <Box
            componentName="date-panel"
            sx={{
              flex: 1,
              padding: 2,
              backgroundColor: "#f5f5f5",
            }}
          >
            {/* CURRENT SECTION */}
            <Box
              componentName="current-section"
              sx={{
                flex: 1,
                backgroundColor: "#ffffff",
                borderRadius: 1,
                p: 2,
              }}
            >
              <Typography
                variant="h6"
                color="primary.dark"
                sx={{ textTransform: "uppercase", mb: 2 }}
              >
                Current
              </Typography>
              {currentState.map(entry => (
                <Box
                  componentName="current-state-entry"
                  key="current-state"
                  sx={{
                    cursor: "pointer",
                    mb: 1,
                    p: 1,
                    borderRadius: 1,
                    backgroundColor:
                      selectedWeek === entry?.date ? "grey.300" : "transparent",
                  }}
                  onClick={() => setSelectedWeek("current state")}
                >
                  <Typography variant="body2">Current State</Typography>
                </Box>
              ))}
            </Box>

            {/* WEEKS SECTION */}
            <Box
              componentName="weeks-section"
              sx={{
                flex: 2,
                backgroundColor: "#ffffff",
                borderRadius: 1,
                p: 2,
                mt: 2,
              }}
            >
              <Typography
                variant="h6"
                color="primary.dark"
                sx={{ textTransform: "uppercase", mb: 2 }}
              >
                Weeks
              </Typography>

              {Object.keys(groupedByWeek).map((weekLabel, index) => (
                <Box
                  componentName={`week-entry-${index}`}
                  key={weekLabel}
                  sx={{
                    cursor: "pointer",
                    mb: 1,
                    p: 1,
                    borderRadius: 1,
                    backgroundColor:
                      weekLabel === selectedWeek ? "grey.300" : "transparent",
                  }}
                  onClick={() => setSelectedWeek(weekLabel)}
                >
                  <Typography variant="body2">{weekLabel}</Typography>
                </Box>
              ))}
            </Box>
          </Box>

          {/* Right Panel */}
          <Box
            componentName="content-panel"
            sx={{
              flex: 2,
              padding: 2,
            }}
          >
            <Typography
              variant="h6"
              color="primary.dark"
              sx={{ textTransform: "uppercase", mb: 2 }}
            >
              Changelog Details
            </Typography>
            {selectedEntries ? (
              <List>
                {selectedEntries.map(entry => (
                  <Box
                    componentName="entry-date"
                    border={false}
                    key={entry.date}
                    sx={{ mb: 2 }}
                  >
                    <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
                      {isValidDate(entry.date)
                        ? new Date(
                            entry.date + "T00:00:00Z",
                          ).toLocaleDateString("en-US", {
                            timeZone: "UTC",
                            year: "numeric",
                            month: "2-digit",
                            day: "2-digit",
                          })
                        : "Current State"}
                    </Typography>

                    {entry.notes.map((note, idx) => (
                      <ListItem key={idx} disablePadding>
                        <ListItemText primary={`- ${note}`} />
                      </ListItem>
                    ))}
                  </Box>
                ))}
              </List>
            ) : (
              <Typography variant="body1">
                Select a date to view details.
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
    </Container>
  );
};
